import { useStaticQuery, graphql } from "gatsby";
import React, { useState, useContext } from "react";
import styled from "styled-components";
import get from "lodash/get";
import { navigate, useIntl } from "gatsby-plugin-intl";
import { withStoreContext } from "gatsby-plugin-shopify-buy";

import { GlobalDispatchContext, GlobalStateContext } from "../../context";
import FlagIcon from "Components/common/flagIcon";
import { StyledArrow } from "Components/common/kcArrow";
import Banner from "./banner";
import BuyButton from "../buyButton";
import SKUCard from "./skuCard";
import Divider from "../divider";
import Row from "./row";
import Topbar from "./topbar";
import ShippingTooltip from "./shippingTooltip";
import CurrencyData from "Components/common/intlPopup/currencyData";
import { parsePrice } from "../../../utils";

const StyledWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  z-index: 999;
  font-family: ${(props) => props.theme.font.sans};
  pointer-events: ${(props) => (props.isShown ? "auto" : "none")};
  display: flex;
  justify-content: flex-end;
  overflow: scroll;
`;

const StyledOverlay = styled.div`
  background-color: ${(props) => props.theme.color.darkGrey};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  z-index: -1;
  opacity: ${(props) => (props.isShown ? "0.6" : "0")};
  pointer-events: ${(props) => (props.isShown ? "auto" : "none")};
  transition: opacity 0.2s ${(props) => (props.isShown ? "0s" : "0.2s")};
`;

const StyledNav = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.white};
  width: 100%;
  opacity: ${(props) => (props.isShown ? "1" : "0")};
  pointer-events: ${(props) => (props.isShown ? "auto" : "none")};
  transition: opacity 0.2s ${(props) => (props.isShown ? "0.2s" : "0s")};
  ${(props) => props.theme.responsive.mobile} {
    width: 26rem;
  }
`;

const StyledContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 3rem;
  ${(props) => props.theme.responsive.mobile} {
    margin: 0;
  }
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${(props) => props.theme.color.lightGrey};
`;

const StyledIntlButton = styled.div`
  display: flex;
  padding: 0.5rem 0.5rem;
  background-color: ${(props) => props.theme.color.smoke};
  border-radius: 2px;
  align-items: center;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const StyledIntlTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 0.4rem;
  margin-right: 0.2rem;
  line-height: 0.8rem;
  text-align: left;
`;

const StyledItemList = styled.div`
  background-color: ${(props) => props.theme.color.white};
  padding: 1rem;
  flex: 0;
  width: 100%;
  padding-bottom: 8.6rem;

  ${(props) => props.theme.responsive.mobile} {
    flex: 1;
    padding-bottom: 0;
  }
`;

const StyledCTAWrapper = styled.div`
  padding: 1rem;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;
  display: ${(props) => (props.isShown ? "block" : "none")};
  background-color: ${(props) => props.theme.color.white};
  ${(props) => props.theme.shadow}
  ${(props) => props.theme.responsive.mobile} {
    position: relative;
  }
`;

const BuyButtonWrapper = styled.div`
  margin-top: 1rem;
`;

const StyledEmptyBagWrapper = styled.div`
  width: 100%;
  height: 20rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledEmptyBagTitle = styled.div`
  font-weight: bold;
  font-size: 2rem;
`;

const StyledEmptyBagCTA = styled.div`
  font-size: 0.7rem;
  margin-top: 0.4rem;
  padding: 0.3rem 0.6rem;
  color: ${(props) => props.theme.color.grey};
  border: 1px solid ${(props) => props.theme.color.lightGrey};
  border-radius: 2px;
  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.color.grey};
    border-color: ${(props) => props.theme.color.grey};
    color: ${(props) => props.theme.color.white};
  }
`;

const BuyPopup = ({ storeContext }) => {
  const intl = useIntl();
  const { allShopifyProductVariant } = useStaticQuery(graphql`
    {
      allShopifyProductVariant(filter: { requiresShipping: { eq: true } }) {
        edges {
          node {
            price
            presentmentPrices {
              price {
                amount
                currencyCode
              }
            }
            shopifyId
            sku
            selectedOptions {
              name
              value
            }
            title
          }
        }
      }
    }
  `);
  const state = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const [stock, setStock] = useState(1);
  const { isBuyPopupShown, shopifyCheckout, currency, currencyCountry } = state;
  const bagFrames = get(shopifyCheckout, "lineItems", []);

  const handleClose = () => {
    dispatch({ type: "SET_IS_BUY_POPUP_SHOWN", isShown: false });
  };

  const handleGoToBrowse = () => {
    dispatch({ type: "SET_IS_BUY_POPUP_SHOWN", isShown: false });
    navigate("/sunglassesv2");
  };

  const getEstimatedShipByDate = () => {
    var curOrderDate = new Date();
    var numWeeks = 1;
    curOrderDate.setDate(curOrderDate.getDate() + 7 * numWeeks);
    let options = {
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return curOrderDate.toLocaleString(intl.locale, options);
  };

  return (
    <StyledWrapper isShown={isBuyPopupShown}>
      <StyledNav isShown={isBuyPopupShown}>
        <Topbar bagFrames={bagFrames} handlerClosePopup={() => handleClose()} />
        <StyledContent>
          <Banner storeContext={storeContext} />
          <StyledBody>
            <StyledItemList>
              {bagFrames.length > 0 ? (
                <>
                  {bagFrames.map((bagFrame, i) => (
                    <SKUCard
                      img={bagFrame.variant.image.src}
                      currency={currency}
                      key={bagFrame.id}
                      curFrame={bagFrame}
                      price={parsePrice(
                        (
                          allShopifyProductVariant?.edges?.find(
                            (v) => v.node.shopifyId === bagFrame.variant.id
                          )?.node?.presentmentPrices || []
                        ).find((p) => p.price.currencyCode === currency)?.price
                          ?.amount
                      )}
                    />
                  ))}

                  <Divider />
                  <Row
                    title={intl.formatMessage({
                      id: `buyPopup.skucard.shippingTo`,
                    })}
                    description={
                      <StyledIntlButton
                        onClick={() =>
                          dispatch({
                            type: "SET_IS_INTL_POPUP_SHOWN",
                            isShown: true,
                          })
                        }
                      >
                        <FlagIcon name={currencyCountry} />
                        <StyledIntlTitle>{currencyCountry}</StyledIntlTitle>
                        <StyledArrow direction="down" size={1} padding={2} />
                      </StyledIntlButton>
                    }
                  />
                  <Row
                    title={intl.formatMessage({
                      id: `buyPopup.skucard.subtotal`,
                    })}
                    description={`${CurrencyData[currency]}${parsePrice(
                      get(shopifyCheckout, "lineItemsSubtotalPrice.amount", 0)
                    )}`}
                  />
                  <Row
                    title={intl.formatMessage({
                      id: `buyPopup.skucard.shipping`,
                    })}
                    description={intl.formatMessage({
                      id: `buyPopup.skucard.free`,
                    })}
                  />
                  <Row
                    title={intl.formatMessage({
                      id: `buyPopup.skucard.taxes`,
                    })}
                    description={intl.formatMessage({
                      id: `buyPopup.skucard.included`,
                    })}
                  />
                  <Row
                    title={intl.formatMessage({
                      id: `buyPopup.skucard.ship_by`,
                    })}
                    description={getEstimatedShipByDate()}
                    tooltip={<ShippingTooltip isShown={true} />}
                  />
                </>
              ) : (
                <StyledEmptyBagWrapper>
                  <StyledEmptyBagTitle>
                    {intl.formatMessage({
                      id: `buyPopup.empty.title`,
                    })}
                  </StyledEmptyBagTitle>
                  <StyledEmptyBagCTA onClick={() => handleGoToBrowse()}>
                    {intl.formatMessage({
                      id: `buyPopup.empty.cta`,
                    })}
                  </StyledEmptyBagCTA>
                </StyledEmptyBagWrapper>
              )}
            </StyledItemList>
          </StyledBody>
          <StyledCTAWrapper isShown={bagFrames.length > 0}>
            {parseFloat(
              get(shopifyCheckout, "lineItemsSubtotalPrice.amount", 0)
            ) -
              parseFloat(get(shopifyCheckout, "totalPriceV2.amount", "0")) >
              0 && (
              <Row
                title={`${intl.formatMessage({
                  id: `buyPopup.summary.discount`,
                })}`}
                description={`-${CurrencyData[currency]}${parsePrice(
                  parseFloat(
                    get(shopifyCheckout, "lineItemsSubtotalPrice.amount", 0)
                  ) -
                    parseFloat(get(shopifyCheckout, "totalPriceV2.amount", "0"))
                )}`}
              />
            )}

            <Row
              title={intl.formatMessage({
                id: `buyPopup.summary.total`,
              })}
              description={`${CurrencyData[currency]}${parsePrice(
                get(shopifyCheckout, "totalPriceV2.amount", "0")
              )}`}
              isBold={true}
            />
            <BuyButtonWrapper>
              <BuyButton isDisabled={!isBuyPopupShown} bagFrames={bagFrames} />
            </BuyButtonWrapper>
          </StyledCTAWrapper>
        </StyledContent>
      </StyledNav>
      <StyledOverlay isShown={isBuyPopupShown} />
    </StyledWrapper>
  );
};

export default withStoreContext(BuyPopup);
